import { useState, useEffect, useCallback } from 'preact/hooks'
import { parseSpotsDisplay, widgetSpotsText } from '../../core/spots-display'
import { apiClient, authStore, InitData } from '../../core'
import { BookingModal, type BookableClass } from '../../components/BookingModal'
import { t, intlLocale, dayNames, formatMoney } from '../../core/i18n'
import { classPriceDisplay } from '../../core/members-only'
import { dagNoegle, dageMellem, fmtNoegle, fmtTid, isoUgenummer, laegDageTil, mandagFor, studietsTidszone, ugedagForNoegle } from '../../core/tidszone'

interface ScheduleEntry {
  /** Fri holdtype på den indloggedes abonnement (OPG-0079): vis «Med i dit abonnement». */
  includedInSubscription?: boolean
  id: string
  name: string
  type?: string | null
  holdTypeColor?: string | null
  startTime: string
  endTime: string
  instructorName: string | null
  room: string | null
  description?: string | null
  maxParticipants?: number | null
  currentParticipants?: number
  dropInPriceDkk?: number
  waitlistEnabled?: boolean
  membershipRequired?: boolean
  excludeFromMembership?: boolean
  /** «Gratis hold» (OPG-0098): ingen betaling, aldrig et klip. */
  isFree?: boolean
  clipCost?: number
  classTemplateId?: string | null
  guestsAllowed?: boolean
  maxGuests?: number
  fullVenueAvailable?: boolean
  fullVenuePriceDkk?: number | null
  /** Holdets egen afmeldingsfrist i timer (null = den generelle), fra widget-API'et. */
  ownCancellationHours?: number | null
  isBooked?: boolean
}

// Mirrors getTypeColor in WeeklyCalendarView.tsx so embedded and CMS-rendered
// ugekalendere fall back to the same per-type palette when a hold type has
// no color configured in the DB.
const TYPE_FALLBACK_COLORS: Record<string, string> = {
  yoga: '#9333ea',
  pilates: '#ec4899',
  yoga_wine: '#f59e0b',
  outdoor_yoga: '#10b981',
  season_yoga: '#f97316',
  special_event: '#22c55e',
}
function resolveTypeColor(entry: ScheduleEntry): string {
  if (entry.holdTypeColor) return entry.holdTypeColor
  if (entry.type && TYPE_FALLBACK_COLORS[entry.type]) return TYPE_FALLBACK_COLORS[entry.type]
  return TYPE_FALLBACK_COLORS.yoga
}

interface Props {
  initData: InitData
  holdTypeSlugs?: string[]
  /** Optional heading rendered above the calendar (widgets carry no title by default). */
  title?: string | null
  /** Show the previous/next-week arrows. Off = the old single-week behaviour. */
  weekNav?: boolean
  /**
   * 'list' (default) = the day-grouped rows every existing embed already renders.
   * 'grid' = the studio site's 7-column weekday grid (Mon..Sun columns), with a
   * day-filter bar so narrow screens can zoom into one day.
   */
  layout?: 'list' | 'grid'
  /** guests-only="true": gæste-fokuseret booking (stepperen vises åben i stedet for
   *  foldet). Videregives til BookingModal. */
  guestsOnly?: boolean
}

// Uger, dage, datoer og tider følger studiets tidszone (initData.timezone), ikke den
// besøgendes browser (core/tidszone.ts). Serveren (/api/widget/weekly-schedule) regner
// weekOffset-ugen i samme tidszone, så kolonnerne og holdene altid passer sammen.

/** Dagnøglen for mandagen i ugen `offset` uger fra i dag, i studiets tidszone. */
function mondayOf(offset: number, tz: string): string {
  return laegDageTil(mandagFor(dagNoegle(new Date(), tz)), offset * 7)
}

/** How many whole weeks ahead `iso` lies, measured Monday-to-Monday in the studio's timezone. */
function weekOffsetOf(iso: string, tz: string): number {
  return Math.round(dageMellem(mondayOf(0, tz), mandagFor(dagNoegle(iso, tz))) / 7)
}

function fmtWeekRange(offset: number, locale: string, tz: string): string {
  const monday = mondayOf(offset, tz)
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  return fmtNoegle(monday, locale, opts) + ' – ' + fmtNoegle(laegDageTil(monday, 6), locale, opts)
}

/**
 * WeeklyScheduleWidget - calendar-style schedule grouped by day.
 *
 * With `weekNav` the visitor can page between weeks, the same way the studio's
 * own ugekalender does. An empty CURRENT week no longer falls back to a flat
 * "next 12 classes" scroll (which is what a season starting weeks from now
 * looked like); instead we jump once to the first week that actually has
 * classes, so the very first paint shows a real week.
 */
export function WeeklyScheduleWidget({ initData, holdTypeSlugs = [], title = null, weekNav = true, layout = 'list', guestsOnly = false }: Props) {
  const s = t(initData.locale)
  const spotsMode = parseSpotsDisplay(initData.features?.spotsDisplay)
  const dateLocale = intlLocale(initData.locale)
  const tz = studietsTidszone(initData.timezone)
  const DAY_LABELS = dayNames(initData.locale)
  const DAY_LABELS_SHORT = dayNames(initData.locale, 'short')
  const [entries, setEntries] = useState<ScheduleEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<ScheduleEntry | null>(null)
  const [weekOffset, setWeekOffset] = useState(0)
  // Grid mode only: null = show every weekday column; 0..6 = zoom into one day
  // (Monday=0). Lets a phone show a single readable column instead of seven.
  const [dayFilter, setDayFilter] = useState<number | null>(null)
  // The auto-jump may only fire on the first load. Once the visitor has used the
  // arrows, an empty week must stay empty - otherwise the widget would yank them
  // back to a different week every time they page past a quiet one.
  const [autoJumpDone, setAutoJumpDone] = useState(false)

  const slugsKey = holdTypeSlugs.join(',')
  const fetchSchedule = useCallback(async () => {
    // Try authenticated first (for isBooked markers). On AUTH_REQUIRED
    // (stale token) silently retry without auth so the widget still works.
    const useAuth = authStore.isLoggedIn()
    const slugsQuery = slugsKey ? `&holdTypeSlugs=${encodeURIComponent(slugsKey)}` : ''
    const weeklyPath = `/api/widget/weekly-schedule?weekOffset=${weekOffset}${slugsQuery}`
    const upcomingPath = `/api/widget/upcoming-classes?limit=12${slugsQuery}`
    const fetchOnce = async (auth: boolean) =>
      apiClient.get<ScheduleEntry[]>(weeklyPath, auth)
    const fetchUpcomingOnce = async (auth: boolean) =>
      apiClient.get<ScheduleEntry[]>(upcomingPath, auth)

    let result = await fetchOnce(useAuth)
    if (useAuth && result.code === 'AUTH_REQUIRED') result = await fetchOnce(false)

    const weeklyFailed = result.error || result.data === undefined
    const weeklyEmpty = result.data && result.data.length === 0

    if (weeklyFailed || weeklyEmpty) {
      let fallback = await fetchUpcomingOnce(useAuth)
      if (useAuth && fallback.code === 'AUTH_REQUIRED') fallback = await fetchUpcomingOnce(false)

      if (weeklyEmpty && weekNav) {
        // Empty week: jump once to the week holding the next class instead of
        // rendering the upcoming list as a scroll.
        const next = fallback.data && fallback.data.length > 0 ? fallback.data[0] : null
        if (!autoJumpDone && weekOffset === 0 && next) {
          const target = weekOffsetOf(next.startTime, tz)
          setAutoJumpDone(true)
          if (target !== 0) {
            setWeekOffset(target)
            return // the offset change refetches; keep the skeleton up
          }
        }
        setEntries([])
        setLoading(false)
        return
      }

      if (fallback.data) {
        setEntries(fallback.data)
      } else if (weeklyFailed) {
        setError(fallback.error || result.error || s.loadError)
      }
    } else if (result.data) {
      setEntries(result.data)
    }
    setLoading(false)
  }, [slugsKey, weekOffset, weekNav, autoJumpDone])

  useEffect(() => {
    setLoading(true)
    fetchSchedule()
  }, [fetchSchedule])

  // Gruppér på datoen (yyyy-mm-dd) i studiets tidszone.
  const grouped = new Map<string, ScheduleEntry[]>()
  for (const entry of entries) {
    const key = dagNoegle(entry.startTime, tz)
    const list = grouped.get(key) || []
    list.push(entry)
    grouped.set(key, list)
  }
  const sortedDays = Array.from(grouped.keys()).sort()

  const modalKlass: BookableClass | null = selected ? {
    id: selected.id,
    name: selected.name,
    startTime: selected.startTime,
    endTime: selected.endTime,
    instructorName: selected.instructorName,
    room: selected.room,
    description: selected.description,
    maxParticipants: selected.maxParticipants,
    currentParticipants: selected.currentParticipants,
    dropInPriceDkk: selected.dropInPriceDkk,
    waitlistEnabled: selected.waitlistEnabled,
    membershipRequired: selected.membershipRequired,
    excludeFromMembership: selected.excludeFromMembership,
    isFree: selected.isFree,
    clipCost: selected.clipCost,
    classTemplateId: selected.classTemplateId,
    guestsAllowed: selected.guestsAllowed,
    maxGuests: selected.maxGuests,
    fullVenueAvailable: selected.fullVenueAvailable,
    fullVenuePriceDkk: selected.fullVenuePriceDkk,
    // Uden denne linje fik booking-vinduet aldrig fristen, og «Særlig afmeldingsfrist»
    // blev aldrig vist i den indlejrede widget (fundet af /ship 14-10-2026).
    ownCancellationHours: selected.ownCancellationHours,
    isBooked: selected.isBooked,
  } : null

  // The header stays mounted across loads so the arrows do not disappear while
  // the next week is being fetched.
  const header = (title || weekNav) ? (
    <div class="hsw-week-header">
      {title && <h2 class="hsw-widget-title">{title}</h2>}
      {weekNav && (
        <div class="hsw-week-nav">
          <button
            type="button"
            class="hsw-week-btn"
            aria-label={s.previousWeek}
            onClick={() => { setAutoJumpDone(true); setWeekOffset(w => Math.max(-52, w - 1)) }}
          >&lsaquo;</button>
          <button
            type="button"
            class={`hsw-week-label${weekOffset === 0 ? ' hsw-week-current' : ''}`}
            title={s.thisWeek}
            onClick={() => { setAutoJumpDone(true); setWeekOffset(0) }}
          >
            {fmtWeekRange(weekOffset, dateLocale, tz)}
          </button>
          <button
            type="button"
            class="hsw-week-btn"
            aria-label={s.nextWeek}
            onClick={() => { setAutoJumpDone(true); setWeekOffset(w => Math.min(52, w + 1)) }}
          >&rsaquo;</button>
        </div>
      )}
    </div>
  ) : null

  // The grid's own head bar. In grid mode `header` is deliberately dropped, so
  // this bar IS the navigation — which is why it must also render on an empty
  // week. Without it a visitor at a tenant with no classes hit a dead end with
  // no way to page anywhere (ship finding, 28-09-2026).
  const gridMonday = mondayOf(weekOffset, tz)
  const gridHead = (
    <div class="hsw-gridhead">
      <div class="hsw-gridhead-left">
        <span class="hsw-gridhead-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </span>
        <div>
          <div class="hsw-gridhead-sub">{s.weekOverview}</div>
          <div class="hsw-gridhead-date">{fmtNoegle(gridMonday, dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
      </div>
      {weekNav && (
        <div class="hsw-gridhead-nav">
          <button type="button" class="hsw-gridhead-today" onClick={() => { setAutoJumpDone(true); setWeekOffset(0) }}>{s.thisWeek}</button>
          <button type="button" class="hsw-gridhead-arrow" aria-label={s.previousWeek} onClick={() => { setAutoJumpDone(true); setWeekOffset(w => Math.max(-52, w - 1)) }}>&lsaquo;</button>
          <span class="hsw-gridhead-week">{s.weekAbbrev} {isoUgenummer(gridMonday)}</span>
          <button type="button" class="hsw-gridhead-arrow" aria-label={s.nextWeek} onClick={() => { setAutoJumpDone(true); setWeekOffset(w => Math.min(52, w + 1)) }}>&rsaquo;</button>
        </div>
      )}
    </div>
  )

  let body
  if (loading) {
    body = (
      <>
        {[0, 1, 2].map(i => (
          <div key={i} class="hsw-day-group">
            <div class="hsw-skel hsw-day-skel-label" />
            <div class="hsw-skel hsw-day-skel-row" />
            <div class="hsw-skel hsw-day-skel-row" />
          </div>
        ))}
      </>
    )
  } else if (error) {
    body = (
      <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
        <strong>{error}</strong>
      </div>
    )
  } else if (!entries.length) {
    const tom = (
      <div class="hsw-empty-week">
        <p>{weekNav ? s.noClassesThisWeek : s.noClassesScheduled}</p>
      </div>
    )
    // Keep the head bar in grid mode so the week arrows survive an empty week.
    body = layout === 'grid' ? <div class="hsw-grid-wrap">{gridHead}{tom}</div> : tom
  } else if (layout === 'grid') {
    // Seven Monday..Sunday columns for the displayed week. Entries are placed by
    // their own local date, so a class always lands under the right weekday even
    // across a DST change. The day-filter narrows to a single column on phones.
    const weekDates = Array.from({ length: 7 }, (_, i) => laegDageTil(mondayOf(weekOffset, tz), i))
    const todayKey = dagNoegle(new Date(), tz)
    const visibleCols = dayFilter === null ? [0, 1, 2, 3, 4, 5, 6] : [dayFilter]
    body = (
      <div class="hsw-grid-wrap">
        {gridHead}
        <div class="hsw-day-filter" role="group">
          <button
            type="button"
            class={`hsw-filter-chip${dayFilter === null ? ' hsw-filter-active' : ''}`}
            aria-pressed={dayFilter === null}
            onClick={() => setDayFilter(null)}
          >{s.filterAll}</button>
          {weekDates.map((d, i) => (
            <button
              key={i}
              type="button"
              class={`hsw-filter-chip${dayFilter === i ? ' hsw-filter-active' : ''}`}
              aria-pressed={dayFilter === i}
              onClick={() => setDayFilter(i)}
            >{DAY_LABELS_SHORT[ugedagForNoegle(d)]}</button>
          ))}
        </div>
        <div class={`hsw-grid${dayFilter !== null ? ' hsw-grid-single' : ''}`}>
          {visibleCols.map(i => {
            const dateKey = weekDates[i]
            const dayEntries = grouped.get(dateKey) || []
            const isToday = dateKey === todayKey
            return (
              <div key={dateKey} class={`hsw-col${isToday ? ' hsw-col-today' : ''}`}>
                <div class="hsw-col-head">
                  <div class="hsw-col-day">{DAY_LABELS_SHORT[ugedagForNoegle(dateKey)]}</div>
                  <div class="hsw-col-date">{fmtNoegle(dateKey, dateLocale, { day: 'numeric', month: 'short' })}</div>
                </div>
                <div class="hsw-col-body">
                  {dayEntries.length === 0 ? (
                    <div class="hsw-col-empty">{s.noClassesDay}</div>
                  ) : (
                    dayEntries.map(entry => {
                      const spotsLeft = (entry.maxParticipants ?? 0) - (entry.currentParticipants ?? 0)
                      const isFull = entry.maxParticipants != null && spotsLeft <= 0
                      const priceKind = classPriceDisplay(entry).kind
                      // Fri holdtype på medlemmets abonnement (OPG-0079): ingen drop-in-pris.
                      const priceText = entry.includedInSubscription
                        ? s.includedInSubscription
                        : priceKind === 'price'
                        ? formatMoney(entry.dropInPriceDkk as number, initData.locale, initData.currency)
                        : priceKind === 'membersOnly' ? s.membershipOnly : s.freeClass
                      const spotsText = entry.maxParticipants != null && !isFull ? widgetSpotsText(spotsLeft, entry.maxParticipants, spotsMode, s, () => `${spotsLeft} ${s.spots}`) : null
                      const subParts = [priceText, spotsText].filter(Boolean) as string[]
                      const ctaLabel = entry.isBooked
                        ? s.booked
                        : isFull
                          ? (entry.waitlistEnabled ? s.waitlist : s.full)
                          : s.book
                      return (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => setSelected(entry)}
                        class={`hsw-grid-card${entry.isBooked ? ' hsw-class-booked' : ''}${isFull ? ' hsw-grid-full' : ''}`}
                        style={{ ['--hsw-type-color' as string]: resolveTypeColor(entry) }}
                      >
                        <div class="hsw-grid-time">{fmtTid(entry.startTime, dateLocale, tz)} – {fmtTid(entry.endTime, dateLocale, tz)}</div>
                        <div class="hsw-grid-name">{entry.name}</div>
                        {entry.description && <div class="hsw-grid-desc">{entry.description}</div>}
                        {(entry.instructorName || entry.room) && (
                          <div class="hsw-grid-meta">{[entry.instructorName, entry.room].filter(Boolean).join(' · ')}</div>
                        )}
                        <span class="hsw-grid-cta">{ctaLabel}</span>
                        {subParts.length > 0 && <div class="hsw-grid-sub">{subParts.join(' · ')}</div>}
                      </button>
                      )
                    })
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  } else {
    body = (
      <>
        {sortedDays.map(dateKey => {
          const dayEntries = grouped.get(dateKey) || []
          const day = ugedagForNoegle(dateKey)
          return (
            <div key={dateKey} class="hsw-day-group">
              <div class="hsw-day-label">{DAY_LABELS[day]} {fmtNoegle(dateKey, dateLocale, { day: 'numeric', month: 'long' })}</div>
              {dayEntries.map(entry => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setSelected(entry)}
                  class={`hsw-class-row${entry.isBooked ? ' hsw-class-booked' : ''}`}
                  style={{ ['--hsw-type-color' as string]: resolveTypeColor(entry) }}
                >
                  <div class="hsw-type-bar" aria-hidden="true" />
                  <div class="hsw-class-time">{fmtTid(entry.startTime, dateLocale, tz)} - {fmtTid(entry.endTime, dateLocale, tz)}</div>
                  <div>
                    <div class="hsw-class-name">{entry.name}</div>
                    {(entry.instructorName || entry.room) && (
                      <div class="hsw-class-meta">
                        {[entry.instructorName, entry.room].filter(Boolean).join(' - ')}
                      </div>
                    )}
                  </div>
                  <div class="hsw-class-cta">{entry.isBooked ? `${s.booked}` : `${s.book}`}</div>
                </button>
              ))}
            </div>
          )
        })}
      </>
    )
  }

  return (
    <div>
      <style>{styles}</style>
      {/* Grid mode drops `header` because the grid has its own head bar with the
          week arrows. The title still has to render: both `holdstyring-week-grid`
          and `weekly-schedule layout="grid"` document a `title` attribute, and it
          silently did nothing here (ship finding, 28-09-2026). */}
      {layout === 'grid'
        ? (title ? <h2 class="hsw-widget-title hsw-grid-title">{title}</h2> : null)
        : header}
      {body}
      {modalKlass && (
        <BookingModal
          klass={modalKlass}
          initData={initData}
          onClose={() => setSelected(null)}
          onBooked={fetchSchedule}
          guestsOnly={guestsOnly}
        />
      )}
    </div>
  )
}

const styles = `
  .hsw-week-header {
    max-width: 1100px;
    margin: 0 auto 1.5rem auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .hsw-widget-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }
  /* In list mode the title sits inside .hsw-week-header, which carries the
     spacing. In grid mode it stands on its own above the head bar. */
  .hsw-grid-title {
    margin: 0 0 0.75rem;
  }
  .hsw-week-nav {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
  }
  .hsw-week-btn,
  .hsw-week-label {
    font: inherit;
    cursor: pointer;
    background: transparent;
    border: 1px solid currentColor;
    border-radius: var(--yoga-radius, 0);
    padding: 0.4rem 0.75rem;
    line-height: 1;
    opacity: 0.75;
  }
  .hsw-week-btn {
    font-size: 1.15rem;
    padding: 0.35rem 0.7rem;
  }
  .hsw-week-label {
    min-width: 11rem;
    text-align: center;
    font-size: 0.9rem;
    font-weight: 600;
  }
  .hsw-week-label.hsw-week-current {
    opacity: 1;
    background: var(--yoga-primary, currentColor);
    color: #fff;
    border-color: transparent;
  }
  .hsw-week-btn:hover,
  .hsw-week-label:hover {
    opacity: 1;
  }
  .hsw-empty-week {
    padding: 3rem;
    text-align: center;
    color: #737373;
  }
  .hsw-day-group {
    margin-bottom: 2.5rem;
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
  .hsw-day-label {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--yoga-primary, #FF6B35);
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--yoga-primary, #FF6B35);
    margin-bottom: 1rem;
  }
  .hsw-class-row {
    display: grid;
    grid-template-columns: 6px 130px 1fr auto;
    gap: 1.5rem;
    align-items: center;
    padding: 1.25rem 1.5rem 1.25rem 0;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    margin-bottom: 0.5rem;
    transition: all .3s;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    text-align: left;
    width: 100%;
    font-family: inherit;
    font-size: inherit;
    overflow: hidden;
  }
  .hsw-type-bar {
    background: var(--hsw-type-color, var(--yoga-primary, #FF6B35));
    align-self: stretch;
    width: 6px;
  }
  .hsw-class-row:hover {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }
  .hsw-class-booked {
    border-color: #16a34a !important;
    background: rgba(22,163,74,0.04);
  }
  .hsw-class-booked .hsw-class-cta {
    color: #16a34a;
  }
  .hsw-class-time {
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    font-size: 0.95rem;
    letter-spacing: -0.01em;
  }
  .hsw-class-name {
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    font-size: 1.05rem;
    margin-bottom: 0.25rem;
    letter-spacing: -0.01em;
  }
  .hsw-class-meta {
    color: var(--yoga-text-muted, #737373);
    font-size: 0.85rem;
  }
  .hsw-class-cta {
    color: var(--yoga-primary, #FF6B35);
    font-weight: 700;
    font-size: 0.9rem;
    white-space: nowrap;
  }
  /* Skeletons */
  .hsw-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }
  .hsw-day-skel-label { height: 14px; width: 30%; margin-bottom: 1rem; }
  .hsw-day-skel-row { height: 56px; width: 100%; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (max-width: 600px) {
    .hsw-class-row {
      grid-template-columns: 6px 90px 1fr auto;
      gap: 1rem;
      padding: 1rem 1rem 1rem 0;
    }
  }
  /* ---- Grid layout (7-column weekday view) ---- */
  .hsw-grid-wrap {
    max-width: 1200px;
    margin: 0 auto;
    color: var(--yoga-text, #1a1a1a);
  }
  /* Header band — mirrors the studio site's "Ugeoversigt" bar. */
  .hsw-gridhead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    background: var(--yoga-header-bg, #3f4657);
    color: #fff;
    padding: 0.9rem 1.25rem;
    border-radius: calc(var(--yoga-radius, 0.5rem) + 6px);
    margin-bottom: 1rem;
  }
  .hsw-gridhead-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .hsw-gridhead-icon {
    display: flex;
    opacity: 0.85;
  }
  .hsw-gridhead-sub {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.7;
  }
  .hsw-gridhead-date {
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .hsw-gridhead-nav {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .hsw-gridhead-today {
    font: inherit;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    background: transparent;
    color: #fff;
    border: none;
    opacity: 0.8;
    padding: 0.3rem 0.5rem;
  }
  .hsw-gridhead-today:hover { opacity: 1; }
  .hsw-gridhead-arrow {
    font: inherit;
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
    color: #fff;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 8px;
    padding: 0.25rem 0.6rem;
  }
  .hsw-gridhead-arrow:hover { background: rgba(255,255,255,0.22); }
  .hsw-gridhead-week {
    font-size: 0.95rem;
    font-weight: 700;
    min-width: 5.5rem;
    text-align: center;
  }
  .hsw-day-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 1rem;
  }
  .hsw-filter-chip {
    font: inherit;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    background: var(--yoga-card-bg, #fff);
    color: var(--yoga-text, #1a1a1a);
    border: 1px solid var(--yoga-card-border, var(--yoga-border, #e5e7eb));
    border-radius: 999px;
    padding: 0.4rem 0.95rem;
    line-height: 1;
    transition: background .15s, color .15s, border-color .15s;
  }
  .hsw-filter-chip:hover {
    border-color: var(--yoga-primary, #FF6B35);
    color: var(--yoga-primary, #FF6B35);
  }
  .hsw-filter-active,
  .hsw-filter-active:hover {
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border-color: var(--yoga-primary, #FF6B35);
  }
  /* One bordered container with thin dividers between the seven day columns. */
  .hsw-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    align-items: stretch;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, var(--yoga-border, #e5e7eb));
    border-radius: calc(var(--yoga-radius, 0.5rem) + 4px);
    overflow: hidden;
  }
  .hsw-grid-single {
    grid-template-columns: minmax(0, 1fr);
    max-width: 520px;
    margin: 0 auto;
  }
  .hsw-col {
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--yoga-card-border, var(--yoga-border, #e5e7eb));
    min-width: 0;
  }
  .hsw-col:last-child { border-right: none; }
  .hsw-col-today {
    background: var(--yoga-accent-50, var(--yoga-primary-50, rgba(255,107,53,0.06)));
  }
  .hsw-col-head {
    text-align: center;
    padding: 0.75rem 0.35rem;
    border-bottom: 1px solid var(--yoga-card-border, var(--yoga-border, #e5e7eb));
  }
  .hsw-col-day {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--yoga-text-secondary, #6b7280);
  }
  .hsw-col-date {
    font-size: 1rem;
    font-weight: 800;
    color: var(--yoga-text, #1a1a1a);
    margin-top: 0.15rem;
    letter-spacing: -0.01em;
  }
  .hsw-col-today .hsw-col-day { color: var(--yoga-accent, var(--yoga-primary, #FF6B35)); }
  .hsw-col-today .hsw-col-date { color: var(--yoga-accent, var(--yoga-primary, #FF6B35)); }
  .hsw-col-body {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.6rem;
    flex: 1;
  }
  .hsw-col-empty {
    color: var(--yoga-text-secondary, #9ca3af);
    font-size: 0.8rem;
    font-style: italic;
    text-align: center;
    padding: 1.75rem 0.25rem;
  }
  .hsw-grid-card {
    display: block;
    width: 100%;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    color: inherit;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, var(--yoga-border, #e5e7eb));
    border-left: 4px solid var(--hsw-type-color, var(--yoga-primary, #FF6B35));
    border-radius: var(--yoga-radius, 0.5rem);
    padding: 0.7rem 0.75rem;
    transition: box-shadow .2s, border-color .2s, transform .1s;
  }
  .hsw-grid-card:hover {
    box-shadow: 0 6px 18px rgba(0,0,0,0.10);
    transform: translateY(-1px);
  }
  .hsw-grid-card.hsw-class-booked {
    border-color: #16a34a !important;
    background: rgba(22,163,74,0.05);
  }
  .hsw-grid-card.hsw-grid-full { opacity: 0.72; }
  .hsw-grid-time {
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--yoga-text-secondary, #6b7280);
  }
  .hsw-grid-name {
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--yoga-text, #1a1a1a);
    margin: 0.2rem 0;
    line-height: 1.25;
    letter-spacing: -0.01em;
  }
  .hsw-grid-desc {
    font-size: 0.75rem;
    font-style: italic;
    color: var(--yoga-text-secondary, #6b7280);
    margin-bottom: 0.4rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-grid-meta {
    font-size: 0.76rem;
    color: var(--yoga-text-secondary, #6b7280);
    margin-bottom: 0.6rem;
  }
  /* Outlined "book" button, full width — fills with the brand colour on hover. */
  .hsw-grid-cta {
    display: block;
    width: 100%;
    text-align: center;
    box-sizing: border-box;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--yoga-primary, #FF6B35);
    background: transparent;
    border: 1.5px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 0.5rem);
    padding: 0.45rem 0.5rem;
  }
  .hsw-grid-card:hover .hsw-grid-cta {
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
  }
  .hsw-grid-card.hsw-class-booked .hsw-grid-cta {
    color: #16a34a;
    border-color: #16a34a;
  }
  .hsw-grid-card.hsw-class-booked:hover .hsw-grid-cta {
    background: #16a34a;
    color: #fff;
  }
  .hsw-grid-card.hsw-grid-full .hsw-grid-cta {
    color: var(--yoga-text-secondary, #6b7280);
    border-color: var(--yoga-card-border, #d1d5db);
  }
  .hsw-grid-card.hsw-grid-full:hover .hsw-grid-cta {
    background: var(--yoga-text-secondary, #6b7280);
    color: #fff;
  }
  .hsw-grid-sub {
    margin-top: 0.5rem;
    text-align: center;
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--yoga-text-secondary, #6b7280);
  }
  /* Below 7 columns worth of room, scroll horizontally so each day stays legible.
     A visitor who wants one day taps a filter chip and gets the single column. */
  @media (max-width: 900px) {
    .hsw-grid:not(.hsw-grid-single) {
      grid-auto-flow: column;
      grid-auto-columns: minmax(165px, 1fr);
      grid-template-columns: none;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x proximity;
    }
    .hsw-grid:not(.hsw-grid-single) .hsw-col {
      scroll-snap-align: start;
    }
    .hsw-gridhead-date { font-size: 1rem; }
  }
`
